import { useCallback } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { ProductCloneResponseDto } from '@tiendanube/common';
import useCanUseUnstableApiV2 from 'App/hooks/useCanUseUnstableApiV2';
import { useAppDispatch } from 'App/store';
import { cleanUpInventory } from 'domains/Catalog/Inventory/inventorySlice';
import { productPayloadBuilder } from 'domains/Catalog/Products/ProductPayloadBuilder/productPayloadBuilder';
import { CloneProductToParamsInterface } from 'domains/Catalog/Products/productsServices';
import {
  deleteProduct as deleteProductAction,
  deleteProductWithUnstableApi as deleteProductWithUnstableApiAction,
  cloneProduct as cloneProductAction,
} from 'domains/Catalog/Products/productsSlice';

export interface UseProductActionsResult {
  deleteProduct: (id: string) => Promise<void>;
  cloneProduct: (
    productToClone: CloneProductToParamsInterface,
  ) => Promise<ProductCloneResponseDto>;
}

function useProductActions(): UseProductActionsResult {
  const dispatch = useAppDispatch();

  const canUseUnstableApiV2 = useCanUseUnstableApiV2();

  const deleteProduct = useCallback(
    async (id: string) => {
      const payload = productPayloadBuilder.delete(id);
      const result = canUseUnstableApiV2
        ? await dispatch(deleteProductWithUnstableApiAction(payload))
        : await dispatch(deleteProductAction(id));
      dispatch(cleanUpInventory());
      return unwrapResult(result);
    },
    [dispatch, canUseUnstableApiV2],
  );

  const cloneProduct = useCallback(
    async (productToClone: CloneProductToParamsInterface) => {
      const result = await dispatch(cloneProductAction(productToClone));
      const productCloneResponse = unwrapResult(result);
      dispatch(cleanUpInventory());
      return productCloneResponse;
    },
    [dispatch],
  );

  return {
    deleteProduct,
    cloneProduct,
  };
}

export default useProductActions;
